import type { MouseEvent } from 'react';
import { EASE_OUT } from '@/components/ui/motion.ts';

/**
 * The inline "Add" composer surfaces three entry types. Kept as an `as const`
 * object so call sites read semantically and the tab ids stay searchable.
 */
export const ComposerTab = {
	Record: 'record',
	Task: 'task',
	Contact: 'contact',
	System: 'system',
} as const;
export type ComposerTab = (typeof ComposerTab)[keyof typeof ComposerTab];

/** Tab render order. The single source of truth for which tabs exist. */
export const COMPOSER_TABS: readonly ComposerTab[] = [ComposerTab.Record, ComposerTab.Task];

/** Pill label per tab. */
export const COMPOSER_TAB_LABEL: Record<ComposerTab, string> = {
	[ComposerTab.Record]: 'Note or file',
	[ComposerTab.Task]: 'Task',
	[ComposerTab.Contact]: 'Contact',
	[ComposerTab.System]: 'System',
};

/** Submit-button label per tab — the action verb changes with the entry type. */
export const COMPOSER_SUBMIT_LABEL: Record<ComposerTab, string> = {
	[ComposerTab.Record]: 'Add',
	[ComposerTab.Task]: 'Add task',
	[ComposerTab.Contact]: 'Add contact',
	[ComposerTab.System]: 'Save system',
};

export const COMPOSER_REPEAT_WHEN_COMPLETED_LABEL = 'Repeat when completed';

/**
 * The composer's action row, named as a size container so the schedule controls inside it can
 * answer to the composer's width rather than the window's: in the workspace the composer sits in a
 * panel the reader can drag narrower without the viewport moving. Declared by every host that draws
 * the row (`ComposerActionRow`, `AssistantInput`, a sub-task's block), since a query with no
 * container to answer it matches nothing.
 */
export const COMPOSER_ACTION_ROW_CONTAINER = 'composer-action-row';
export const COMPOSER_ACTION_ROW_CONTAINER_CSS = {
	containerType: 'inline-size',
	containerName: COMPOSER_ACTION_ROW_CONTAINER,
} as const;
/** The action row at least this wide, as a `css` key. */
export const composerRowAtLeast = (minWidth: string) =>
	`@container ${COMPOSER_ACTION_ROW_CONTAINER} (min-width: ${minWidth})`;

/**
 * Where the repeat toggle's label gives way, measured against the whole row: the 136px due-date
 * field beside it, the full sentence about 250px of pill, "Repeat" about 140, the icon and track
 * alone about 80.
 *
 * TODO: Base Repeat's bands on the schedule controls' available width, not the whole action row.
 * At a 460px panel width the non-recurring toggle overlaps Submit even with sub-tasks disabled;
 * enabling sub-tasks adds overlaps at other widths. Add button-bounds tests with the flag on/off.
 */
export const COMPOSER_REPEAT_FULL_LABEL_MIN_WIDTH = '420px';
export const COMPOSER_REPEAT_SHORT_LABEL_MIN_WIDTH = '300px';
/**
 * Submit takes two label steps: full verb -> short verb below 480px -> icon below 380px.
 * File and Email go straight from word to icon at 480px. Private loses its word there too,
 * then draws in its padding below 380px; it has no intermediate short label.
 */
export const COMPOSER_PILL_LABEL_MIN_WIDTH = '480px';
export const COMPOSER_PILL_SHORT_MIN_WIDTH = '380px';

/** A labelled action pill becomes a round icon button when its row no longer has room for words. */
export const COMPOSER_COLLAPSIBLE_PILL_LABEL_CSS = {
	display: 'none',
	[composerRowAtLeast(COMPOSER_PILL_LABEL_MIN_WIDTH)]: { display: 'inline' },
} as const;
export const COMPOSER_COLLAPSIBLE_PILL_CSS = {
	paddingInlineStart: '0',
	paddingInlineEnd: '0',
	aspectRatio: '1',
	[composerRowAtLeast(COMPOSER_PILL_LABEL_MIN_WIDTH)]: {
		paddingInlineStart: '12px',
		paddingInlineEnd: '16px',
		aspectRatio: 'auto',
	},
} as const;

/**
 * The optional sub-task action drops its word below this action-row width. The frequency label
 * independently measures its own remaining column, so submit and sub-task widths are accounted for.
 */
export const COMPOSER_SUBTASK_LABEL_MIN_WIDTH = '540px';
export const COMPOSER_SUBTASK_LABEL_CSS = {
	display: 'none',
	[composerRowAtLeast(COMPOSER_SUBTASK_LABEL_MIN_WIDTH)]: { display: 'inline' },
} as const;
export const COMPOSER_SUBTASK_PILL_CSS = {
	paddingInlineStart: '0',
	paddingInlineEnd: '0',
	aspectRatio: '1',
	[composerRowAtLeast(COMPOSER_SUBTASK_LABEL_MIN_WIDTH)]: {
		paddingInlineStart: '12px',
		paddingInlineEnd: '16px',
		aspectRatio: 'auto',
	},
} as const;

/** The submit's shorter verb, for a row with room for a word but not the noun. */
export const COMPOSER_SUBMIT_SHORT_LABEL: Record<ComposerTab, string> = {
	[ComposerTab.Record]: 'Add',
	[ComposerTab.Task]: 'Add',
	[ComposerTab.Contact]: 'Add',
	[ComposerTab.System]: 'Save',
};

/**
 * The + button's diameter, which doubles as the bar's collapsed control height
 * so the two bottom-align with matching centres. 52px matches the records-page
 * AddButton, keeping the surfaces paired.
 */
export const COMPOSER_ADD_BUTTON_SIZE = 52;

// How far the + overhangs the bar's left edge on desktop (button width + its gap to the bar).
export const COMPOSER_ADD_BUTTON_OVERHANG = COMPOSER_ADD_BUTTON_SIZE + 12;
// Breathing room the + keeps from the inset card's wall, so it never sits hard against it.
export const COMPOSER_ADD_BUTTON_GUTTER = 16;
/** One-pixel top + bottom borders on the composer surface (border-box height accounting). */
export const COMPOSER_SURFACE_VERTICAL_BORDER_PX = 2;
export const COMPOSER_ADD_FADE_LEAD_MS = 60;
export const COMPOSER_ACTION_ROW_SLIDE_MS = 300;
export const COMPOSER_ATTACH_SLIDE_PX = 0;
export const COMPOSER_ACTION_ROW_SUBMIT_SLIDE_DISTANCE_PX = 0;
export const COMPOSER_PRIVACY_SLIDE_PX = 0;
export const COMPOSER_TOOLTIP_OPEN_DELAY_MS = 150;
export const COMPOSER_TOOLTIP_CLOSE_DELAY_MS = 100;
export const COMPOSER_SUBMIT_MORPH_SETTLE_MS = 60;
export const COMPOSER_SUBMIT_CROSSFADE_MS = 200;
export const COMPOSER_SUBMIT_REDUCED_MOTION_FADE_MS = 150;
/** Direct cancel action: quicker than opening/closing an entire composer region. */
export const COMPOSER_CHILD_REMOVE_MS = 120;
/**
 * dvh (dynamic viewport height) so the sheet tracks the collapsing mobile address bar instead of
 * jumping. The keyboard inset is handled separately (useKeyboardInset) — dvh ignores the keyboard.
 */
export const COMPOSER_ADD_SHEET_HEIGHT = {
	Compact: '56dvh',
	Expanded: '88dvh',
} as const;
export const COMPOSER_ADD_SHEET_TRANSITION = `height 220ms ${EASE_OUT}, max-height 220ms ${EASE_OUT}`;
export const COMPOSER_ADD_REGION_VIEWPORT_GAP = 16;
export const COMPOSER_ADD_REGION_MIN_SCROLL_HEIGHT = 160;
export const COMPOSER_PORTAL_CLICK_BOUNDARY_ATTR = 'data-composer-portal-click-boundary';
export const COMPOSER_PORTAL_CLICK_BOUNDARY_SELECTOR = `[${COMPOSER_PORTAL_CLICK_BOUNDARY_ATTR}]`;

/**
 * Spread onto any portalled surface (popover/drawer) that should NOT count as an outside click for
 * the composer's click-away. The data attribute lets the click-away matcher recognise the boundary
 * via `composedPath`, and the capture-phase stopPropagation covers listeners that don't.
 */
export const COMPOSER_PORTAL_CLICK_BOUNDARY_PROPS = {
	[COMPOSER_PORTAL_CLICK_BOUNDARY_ATTR]: '',
	onMouseDownCapture: (event: MouseEvent) => event.stopPropagation(),
} as const;

/**
 * Shared width for the docked bar and the chat transcript, so they stay the same size and aligned.
 * On desktop it's fluid — capped at 720 but shrinking with the viewport — and reserves the +
 * overhang plus a gutter on both sides (to stay centred). Reserving overhang + gutter guarantees
 * the + always clears the inset wall by at least the gutter, at every width. Without this the fixed
 * 720 is wider than the space left by the sidebar at ~750-1100px, so it cramps/overflows instead of
 * shrinking down gracefully.
 */
export const ASSISTANT_CONTENT_WIDTH = {
	base: '100%',
	// Cap at 540 but stay fluid: a fixed 540px overflows the viewport from ~480–570px (sm starts at
	// 480px) and pushes the bar off the right edge. min() shrinks it to fit below that width.
	sm: 'min(540px, 100%)',
	md: `min(720px, calc(100% - ${(COMPOSER_ADD_BUTTON_OVERHANG + COMPOSER_ADD_BUTTON_GUTTER) * 2}px))`,
} as const;

/**
 * Cap for the auto-growing ask query, and for the note and task details on desktop, before they
 * scroll internally. In the mobile sheet the details are uncapped: the sheet body is the scroller,
 * and a second one inside it put two scrollbars on a long note.
 */
export const COMPOSER_TEXTAREA_MAX_H = 320;
export const COMPOSER_ENTRY_TITLE_FONT_SIZE = '1.125rem';
export const COMPOSER_ENTRY_TITLE_FONT_WEIGHT = 600;
export const COMPOSER_ENTRY_TITLE_LINE_HEIGHT = 1.25;
export const COMPOSER_ENTRY_TITLE_PADDING_TOP = '4px';
export const COMPOSER_ENTRY_BODY_FONT_SIZE = '0.9375rem';
export const COMPOSER_ENTRY_BODY_FONT_WEIGHT = 400;
export const COMPOSER_ENTRY_BODY_LINE_HEIGHT = 1.6;
export const COMPOSER_ENTRY_BODY_PADDING_TOP = '6px';
export const COMPOSER_ENTRY_BODY_PADDING_BOTTOM = '12px';
/** Desktop only: two lines of details plus the row's padding, so a note or task opens with room to write in. */
export const COMPOSER_ENTRY_BODY_MIN_H = `calc(2 * ${COMPOSER_ENTRY_BODY_FONT_SIZE} * ${COMPOSER_ENTRY_BODY_LINE_HEIGHT} + ${COMPOSER_ENTRY_BODY_PADDING_TOP} + ${COMPOSER_ENTRY_BODY_PADDING_BOTTOM})`;
export const COMPOSER_ENTRY_ROW_GAP = '6px';
export const COMPOSER_ENTRY_DETAILS_PLACEHOLDER = 'Add details...';

/**
 * scrollHeight past which the ask field reads as multi-line. Single line ≈ lineHeight (20px) +
 * py={2} (16px) = 36px; the 4px buffer avoids flipping on sub-pixel jitter. Mirrors SearchOrAskBar.
 */
export const COMPOSER_MULTILINE_THRESHOLD_PX = 40;

/**
 * Height of the interactive controls in the add bar (due-date field, repeat toggle, frequency
 * selects, submit). 44px on mobile for comfortable touch targets, 36px on desktop where it's tighter.
 */
export const COMPOSER_BAR_CONTROL_H = { base: 11, md: 9 } as const;

export interface ComposerEntryPlaceholder {
	title: string;
	body: string;
	bodyAfterTitle?: string;
}

export const COMPOSER_NOTE_PLACEHOLDER: Record<
	'MobilePrivate' | 'MobilePublic' | 'DesktopPrivate' | 'DesktopPublic',
	ComposerEntryPlaceholder
> = {
	MobilePrivate: {
		title: 'Add a private note...',
		body: 'or upload a private file...',
		bodyAfterTitle: COMPOSER_ENTRY_DETAILS_PLACEHOLDER,
	},
	MobilePublic: {
		title: 'Add a note...',
		body: 'or upload a file',
		bodyAfterTitle: COMPOSER_ENTRY_DETAILS_PLACEHOLDER,
	},
	DesktopPrivate: {
		title: 'Add a private note...',
		body: 'or drop in a private file',
		bodyAfterTitle: COMPOSER_ENTRY_DETAILS_PLACEHOLDER,
	},
	DesktopPublic: {
		title: 'Add a note...',
		body: 'or drop in a file',
		bodyAfterTitle: COMPOSER_ENTRY_DETAILS_PLACEHOLDER,
	},
} as const;

export const getComposerNotePlaceholder = (isMobile: boolean, isPrivate: boolean): ComposerEntryPlaceholder => {
	if (isMobile) {
		return isPrivate ? COMPOSER_NOTE_PLACEHOLDER.MobilePrivate : COMPOSER_NOTE_PLACEHOLDER.MobilePublic;
	}

	return isPrivate ? COMPOSER_NOTE_PLACEHOLDER.DesktopPrivate : COMPOSER_NOTE_PLACEHOLDER.DesktopPublic;
};

/**
 * Bar morph: a pill when collapsed, a rounded card once the add forms (or a
 * multi-line query) make it taller. The collapsed radius is a CONCRETE value
 * (half the control height → a true pill), NOT `full`. Animating from `full`
 * (≈9999px) morphs the corners through mush, because the rendered radius is
 * capped to height/2 and tracks the growing height until the animated value
 * drops below it. Concrete radii stay below height/2 throughout, so the corners
 * animate cleanly the whole way.
 */
export const COMPOSER_COLLAPSED_RADIUS = `${COMPOSER_ADD_BUTTON_SIZE / 2}px`;
export const COMPOSER_EXPANDED_RADIUS = '3xl';
export const TRANSITION_COMPOSER_MORPH =
	'border-radius 0.3s ease, border-color 0.15s ease, box-shadow 0.15s ease, background-color 0.15s ease';

// Matches SearchOrAskBar's _focusWithin elevation so the two bars stay paired.
export const COMPOSER_FOCUS_SHADOW = '0 2px 4px rgba(0, 0, 0, 0.05), 0 3px 6px rgba(0, 0, 0, 0.05)';
/**
 * The + → X rotates on click, so it's a click-initiated response, not free on-screen motion —
 * ease-out (fast start) makes it feel instant. An ease-in-out curve starts slow, which reads as
 * lag at the exact moment the user is watching after the click.
 */
export const TRANSITION_ADD_BUTTON_ROTATE = `transform 0.25s ${EASE_OUT}`;
